/**
 * Price localization for the Minuta Pro plan (monthly and annual).
 *
 * Source of truth: BRL — Stripe charges in BRL because the Stripe account is
 * registered in Portugal and `currency_options` cannot be enabled for it.
 * USD and EUR values are **display-only**, set close to current FX so the
 * marketing copy stays honest. The customer's card issuer converts BRL into
 * the local currency at its daily rate when the charge clears.
 *
 * Annual plan: billed as a single charge per year. The per-month equivalent
 * is shown in the UI as the headline price when the annual toggle is selected.
 *
 * Currency is picked from the visitor's country signal:
 *   any browser locale with region BR → BRL
 *   every other visitor              → USD
 *
 * The user can override via the <CurrencyPicker/> component; the choice is
 * persisted in localStorage under `minuta.currency`.
 */

export type Currency = "BRL" | "USD" | "EUR";

export interface CurrencyInfo {
  code: Currency;
  symbol: string;
  /** Monthly price for the Pro plan, in the currency's major unit. */
  proMonthly: number;
  /** What we display in the copy — a rounded, marketing-friendly string. */
  proFormatted: string;
  locale: string;
  /** Total annual charge (e.g. 350 for BRL). */
  proAnnualTotal: number;
  /** Per-month equivalent shown as the headline under the annual toggle. */
  proAnnualMonthlyFormatted: string;
  /** Side-note total displayed below the headline (e.g. "R$ 350/year"). */
  proAnnualTotalFormatted: string;
  /** Integer savings percentage shown on the annual badge (e.g. 17). */
  proAnnualSavingsPct: number;
  /** Per-month Founding 100 price displayed alongside the badge/CTA. */
  foundingMonthly: number;
  /** Marketing-friendly Founding 100 price (e.g. "R$ 22,50"). */
  foundingPriceFormatted: string;
}

export const CURRENCIES: Record<Currency, CurrencyInfo> = {
  BRL: {
    code: "BRL",
    symbol: "R$",
    proMonthly: 35,
    proFormatted: "R$ 35",
    locale: "pt-BR",
    proAnnualTotal: 350,
    proAnnualMonthlyFormatted: "R$ 29",
    proAnnualTotalFormatted: "R$ 350",
    proAnnualSavingsPct: 17,
    foundingMonthly: 22.5,
    foundingPriceFormatted: "R$ 22,50",
  },
  USD: {
    code: "USD",
    symbol: "$",
    proMonthly: 7,
    proFormatted: "$7",
    locale: "en-US",
    proAnnualTotal: 60,
    proAnnualMonthlyFormatted: "$5",
    proAnnualTotalFormatted: "$60",
    proAnnualSavingsPct: 29,
    foundingMonthly: 4.5,
    foundingPriceFormatted: "$4.50",
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    proMonthly: 6.5,
    proFormatted: "€6.50",
    locale: "de-DE",
    proAnnualTotal: 55,
    proAnnualMonthlyFormatted: "€4.58",
    proAnnualTotalFormatted: "€55",
    proAnnualSavingsPct: 29,
    foundingMonthly: 4,
    foundingPriceFormatted: "€4",
  },
};

/**
 * Detect the default currency from browser locale country hints. Region beats
 * language: `pt-BR`, `en-BR`, and `es-BR` default to BRL; `pt-PT`, `es-ES`,
 * and every non-Brazil locale default to USD.
 */
export function detectCurrency(): Currency {
  if (typeof navigator === "undefined") return "USD";
  const languages = [
    ...Array.from(navigator.languages ?? []),
    navigator.language || "",
  ].filter(Boolean);
  return languages.some(isBrazilLocale) ? "BRL" : "USD";
}

/**
 * Map a locale tag to the default display currency. Kept for callers/tests
 * that pass a single locale, but this is country-based rather than
 * language-based.
 */
export function currencyForLang(lang: string): Currency {
  return isBrazilLocale(lang) ? "BRL" : "USD";
}

function isBrazilLocale(locale: string): boolean {
  return localeRegion(locale) === "BR";
}

function localeRegion(locale: string): string | null {
  const normalized = (locale || "").trim().replace(/_/g, "-");
  if (!normalized) return null;

  try {
    const region = new Intl.Locale(normalized).region;
    if (region) return region.toUpperCase();
  } catch {
    /* fall through to string parsing */
  }

  const parts = normalized.split("-");
  for (const part of parts.slice(1)) {
    if (/^[a-z]{2}$/i.test(part) || /^\d{3}$/.test(part)) {
      return part.toUpperCase();
    }
  }
  return null;
}

const STORAGE_KEY = "minuta.currency";

/**
 * Returns the currency to display, in order of precedence:
 *   1. The user's saved choice in localStorage.
 *   2. The navigator-derived country default (BR → BRL, otherwise USD).
 *
 * The optional `lang` argument is retained for compatibility with older
 * callsites, but site language no longer influences the automatic default.
 */
export function loadCurrency(lang?: string): Currency {
  if (typeof window === "undefined") {
    void lang;
    return "USD";
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in CURRENCIES) return stored as Currency;
  } catch {
    /* storage unavailable */
  }
  void lang;
  return detectCurrency();
}

export function saveCurrency(c: Currency) {
  try {
    localStorage.setItem(STORAGE_KEY, c);
  } catch {
    /* ignore */
  }
}
